import type { BreadcrumbProps } from '@blueprintjs/core'
import { Card } from '../../components/ui/card'
import classNames from 'classnames'
import { observer } from 'mobx-react-lite'
import React, { useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Layout } from '../../components/Layout'
import { LogsTable } from '../../components/logs/LogsTable'
import { TableStore } from '../../components/table2/table_store'
import { useSavedFiltersStore } from '../../hooks/useSavedFiltersStore'
import { useUser } from '../../hooks/useUser'
import { useTableHooksFromStore } from '../../packages/blueprint-components/src/useTableHooks'
import { userStore } from '../../stores/user_store'
import { axiosGet } from '../../util/api'
import { isNavV2Enabled } from '../../util/nav-version'
import { AuditLogsTable } from '../audit_logs'
import { UserDataSyncs } from '../user_data_syncs'
import { Combobox } from '../../components/ui/combobox'
import { ScheduledRunsTable } from '../../features/scheduled-segments/components/ScheduledRunsTable'

type View = 'Smart Campaigns' | 'All Other History' | 'Data Syncs' | 'Scheduled segments'
type ViewSlug = 'smart-campaigns' | 'data-syncs' | 'all-other-history' | 'scheduled-segments'

const viewOptions: { label: View; value: ViewSlug }[] = [
    { label: 'Smart Campaigns', value: 'smart-campaigns' },
    { label: 'Data Syncs', value: 'data-syncs' },
    { label: 'Scheduled segments', value: 'scheduled-segments' },
    { label: 'All Other History', value: 'all-other-history' },
]

const viewBySlug = viewOptions.reduce(
    (acc, option) => {
        acc[option.value] = option.label
        return acc
    },
    {} as Record<ViewSlug, View>,
)

const isViewSlug = (value: string | null): value is ViewSlug =>
    value === 'smart-campaigns' ||
    value === 'data-syncs' ||
    value === 'scheduled-segments' ||
    value === 'all-other-history'

export const CombinedHistory = observer(() => {
    const { profile } = useUser()
    const { pathname, search } = useLocation()
    const navigate = useNavigate()
    const useNavV2 = isNavV2Enabled(search)
    const query = useMemo(() => new URLSearchParams(search), [search])
    const requestedView = query.get('view')
    const viewSlug: ViewSlug = isViewSlug(requestedView) ? requestedView : 'all-other-history'
    const view = viewBySlug[viewSlug]
    const [logsStore] = useState(new TableStore<any>(userStore, `logs_for_history`))
    const savedFiltersStore = useSavedFiltersStore()
    const logsLoad = (params) => {
        if (!profile) {
            return Promise.resolve()
        }

        return axiosGet('logs', params)
    }
    const logsTableHooks = useTableHooksFromStore(logsLoad, {}, false, logsStore)

    const breadcrumbs: BreadcrumbProps[] = [{ text: 'Overview' }]
    const setViewSlug = (nextView: string) => {
        if (!isViewSlug(nextView)) {
            return
        }

        const nextQuery = new URLSearchParams(search)
        if (nextView === 'all-other-history') {
            nextQuery.delete('view')
        } else {
            nextQuery.set('view', nextView)
        }

        const nextSearch = nextQuery.toString()
        navigate(`${pathname}${nextSearch ? `?${nextSearch}` : ''}`)
    }
    const historyTypeSelect = (
        <Combobox
            options={viewOptions}
            value={viewSlug}
            onChange={setViewSlug}
            minimal
            className="min-w-[172px]"
            caretAlign="right"
        />
    )

    return (
        <Layout breadcrumbs={breadcrumbs} primary={view}>
            {view == 'Smart Campaigns' && (
                <Card
                    className={classNames(
                        'flex flex-col bp4-card-rounded p-0',
                        useNavV2 && '!rounded-none !shadow-none mt-0',
                    )}
                    style={{ boxShadow: useNavV2 ? 'none' : undefined }}
                >
                    <LogsTable
                        nested
                        store={logsStore}
                        savedFiltersStore={savedFiltersStore}
                        tableHooks={logsTableHooks}
                        leadingActions={historyTypeSelect}
                    />
                </Card>
            )}
            {view == 'All Other History' && (
                <AuditLogsTable bootstrapped leadingActions={historyTypeSelect} />
            )}
            {view == 'Data Syncs' && <UserDataSyncs leadingActions={historyTypeSelect} />}
            {view == 'Scheduled segments' && (
                <ScheduledRunsTable leadingActions={historyTypeSelect} />
            )}
        </Layout>
    )
})
